import { cn } from '@/lib/utils';

/**
 * The row of filters above a list — "All · Scheduled · In Progress · Completed".
 *
 * The prototype uses this above its leads inbox and it reads well. Two things
 * it needs that a row of styled buttons does not give you for free:
 *
 *   - **A count on each pill**, so "Completed 0" is visible before it is
 *     clicked. Zero is `0` and worth showing — clicking a filter to discover it
 *     is empty is a wasted trip.
 *   - **`role="tablist"`**, because that is what this is: one of a set, exactly
 *     one selected, arrow keys between them.
 */

export interface FilterOption<Value extends string> {
  readonly value: Value;
  /** Humanised. Never a slug — defect P5. */
  readonly label: string;
  /** Undefined means not counted yet, which renders as nothing rather than 0. */
  readonly count?: number;
}

export function FilterPills<Value extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: readonly FilterOption<Value>[];
  value: Value;
  onChange: (value: Value) => void;
  /** What is being filtered. "Filter jobs by status". */
  label: string;
}) {
  return (
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => {
              onChange(option.value);
            }}
            className={cn(
              // `min-w-touch` as well as `min-h-touch`: 44×44 is a square, not a
              // height. A short label like "All" is 43px wide at `px-3.5` on
              // Linux font metrics — it passed on a Mac and failed the 375px
              // sweep in CI, which is the whole reason that sweep measures
              // rendered boxes rather than reading the classes.
              'inline-flex min-h-touch min-w-touch items-center justify-center gap-1.5 rounded-full px-3.5 text-label',
              selected
                ? // ink-900 on honey-400, 14.75:1. Never honey as a text colour.
                  'bg-honey-400 text-ink-900'
                : 'border border-ink-300 bg-white text-ink-700 hover:bg-honey-50',
            )}
          >
            {option.label}
            {option.count === undefined ? null : (
              <span
                className={cn(
                  'rounded-full px-1.5 text-meta',
                  selected ? 'bg-ink-900/10' : 'bg-ink-100',
                )}
              >
                {option.count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
